import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

/**
 * Reusable, lightweight comments thread for a single deliverable version.
 * Backend endpoints:
 *  - agency: POST /clients/{cid}/deliverables/{did}/versions/{vid}/comments
 *  - public: POST /public/portal/{token}/deliverables/{did}/versions/{vid}/comments
 * The caller wires `onSubmit(text)` which posts to the correct URL and
 * returns the created comment. We optimistically render on success.
 */
export default function CommentsBlock({
    comments = [],
    versionLabel,
    onSubmit,
    submitLabel = "Send",
    testidPrefix = "comments",
    accent = "#C55A46",
}) {
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState(comments);
    const listRef = useRef(null);

    // Keep in sync with the incoming prop (e.g. after a reload/refresh)
    useEffect(() => { setItems(comments || []); }, [comments]);

    const send = async (e) => {
        e?.preventDefault?.();
        const trimmed = text.trim();
        if (!trimmed) {
            toast.error("Comment cannot be empty");
            return;
        }
        if (trimmed.length > 2000) {
            toast.error("Comment is too long", { description: "Max 2000 characters." });
            return;
        }
        setSaving(true);
        try {
            const created = await onSubmit(trimmed);
            if (created) setItems((prev) => [...prev, created]);
            setText("");
            // scroll to newest
            setTimeout(() => {
                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
            }, 30);
        } catch (err) {
            toast.error("Could not post comment", { description: err?.message || "" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3"
             data-testid={`${testidPrefix}-block`}>
            <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-3.5 h-3.5 text-slate-500" />
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Comments{versionLabel ? ` · ${versionLabel}` : ""}
                    {items.length > 0 && (
                        <span className="ml-1 text-slate-400 normal-case tracking-normal">({items.length})</span>
                    )}
                </p>
            </div>

            {items.length > 0 && (
                <ul ref={listRef}
                    data-testid={`${testidPrefix}-list`}
                    className="space-y-2 mb-3 max-h-64 overflow-y-auto pr-1">
                    {items.map((c) => (
                        <li key={c.comment_id}
                            data-testid={`${testidPrefix}-item-${c.comment_id}`}
                            className="rounded-md bg-white border border-slate-200 p-2.5">
                            <div className="flex items-center gap-2">
                                <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${
                                        c.author_type === "agency"
                                            ? "text-white border-transparent"
                                            : "bg-slate-100 text-slate-700 border-slate-200"
                                    }`}
                                    style={c.author_type === "agency" ? { backgroundColor: accent } : undefined}
                                >
                                    {c.author_type === "agency" ? "Agency" : "Client"}
                                </span>
                                <span className="text-xs text-slate-700 font-medium truncate">{c.author_name || "—"}</span>
                                <span className="text-[11px] text-slate-400 ml-auto">{formatSafe(c.created_at)}</span>
                            </div>
                            <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                                {c.text}
                            </p>
                        </li>
                    ))}
                </ul>
            )}

            <form onSubmit={send} className="flex gap-2 items-start">
                <Textarea
                    rows={2}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid={`${testidPrefix}-input`}
                    className="ring-terra flex-1 min-h-[42px] text-sm"
                    placeholder="Write a comment…"
                    maxLength={2000}
                    disabled={saving}
                />
                <button
                    type="submit"
                    disabled={saving || !text.trim()}
                    data-testid={`${testidPrefix}-submit`}
                    className="btn-terra rounded-lg px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ backgroundColor: accent }}
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {submitLabel}
                </button>
            </form>
        </div>
    );
}

function formatSafe(iso) {
    if (!iso) return "";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
    catch { return iso; }
}
